import { ProjectCollection, ImageGroupCollection, ImageInstanceCollection, Project } from '@/api';

const state = {
  projects: [],
  selectedItem: null,
  selectedItemType: null,
  selectedProject: null,
  representatives: [],
  onlines: [],
  all: true,
  searchString: '',
  // Content display state
  contentLoading: false,
  images: [],
  imageGroups: [],
  activeImageGroupForViewer: null,
  // Sorting state
  sortField: 'name', // Default sort field
  sortOrder: null,  // Default sort order (asc, desc, null)
  // Pagination State for Content Display
  currentPage: 1,
  perPage: 15,
  totalImages: 0,
  // Pagination State for Project Tree
  projectCurrentPage: 1,
  projectPerPage: 20,
  totalProjects: 0,
};

const mutations = {
  SET_PROJECTS(state, projects) {
    state.projects = projects;
  },
  SET_PROJECT_DETAILS(state, { representatives, onlines }) {
    state.representatives = representatives;
    state.onlines = onlines;
  },
  SET_SELECTED_ITEM(state, { item, type }) {
    state.selectedItem = item;
    state.selectedItemType = type;
    if (type === 'project') {
      state.selectedProject = item;
    }
  },
  SET_SELECTED_PROJECT(state, project) {
    state.selectedProject = project;
  },
  SET_ALL_FILTER(state, all) {
    state.all = all;
  },
  SET_SEARCH_STRING(state, searchString) {
    state.searchString = searchString;
  },
  SET_SORT_ORDER(state, order) {
    state.sortOrder = order;
  },
  ADD_IMAGE_GROUP_TO_PROJECT(state, { project, imageGroup }) {
    const proj = state.projects.find(p => p.id === project.id);
    if (proj) {
      proj.imageGroups.push(imageGroup);
      proj.isExpanded = true;
    }
  },
  EXPAND_PROJECT(state, projectToExpand) {
    const project = state.projects.find(p => p.id === projectToExpand.id);
    if (project) {
      project.isExpanded = true;
    }
  },
  TOGGLE_PROJECT_EXPANSION(state, project) {
    const proj = state.projects.find(p => p.id === project.id);
    if (proj) {
      proj.isExpanded = !proj.isExpanded;
    }
  },
  // Content display mutations
  SET_CONTENT_LOADING(state, loading) {
    state.contentLoading = loading;
  },
  SET_CONTENT_DATA(state, { images, imageGroups }) {
    state.images = images;
    state.imageGroups = imageGroups;
  },
  SET_ACTIVE_IMAGE_GROUP_FOR_VIEWER(state, groupId) {
    state.activeImageGroupForViewer = groupId;
  },
  SET_CURRENT_PAGE(state, page) {
    state.currentPage = page;
  },
  SET_PER_PAGE(state, perPage) {
    state.perPage = perPage;
  },
  SET_TOTAL_IMAGES(state, total) {
    state.totalImages = total;
  },
  SET_PROJECT_CURRENT_PAGE(state, page) {
    state.projectCurrentPage = page;
  },
  SET_PROJECT_PER_PAGE(state, perPage) {
    state.projectPerPage = perPage;
  },
  SET_TOTAL_PROJECTS(state, total) {
    state.totalProjects = total;
  },
};

const actions = {
  async fetchProjects({ state, commit }) {
    try {
      const expandedProjectIds = state.projects
        .filter(p => p.isExpanded)
        .map(p => p.id);

      // Assemble collection parameters
      let collectionParams = {
        all: state.all,
        withImageGroups: true,
        nbPerPage: state.projectPerPage
      };

      if (state.searchString && state.searchString.trim() !== '') {
        collectionParams.name = {
          ilike: encodeURIComponent(state.searchString.trim())
        };
      }

      // Add sorting parameters if sortOrder is active
      if (state.sortOrder) {
        collectionParams.sort = state.sortField;
        collectionParams.order = state.sortOrder;
      }

      const projectCollection = new ProjectCollection(collectionParams);
      await projectCollection.fetchPage(state.projectCurrentPage - 1);
      const initialProjects = projectCollection.array;
      commit('SET_TOTAL_PROJECTS', projectCollection.totalNbItems);

      // Create projects with image groups data
      let newProjects = initialProjects.map(p => ({
        ...p,
        imageGroups: p.imageGroups || [],
        isExpanded: expandedProjectIds.includes(p.id)
      }));

      console.log('Projects:', newProjects);

      commit('SET_PROJECTS', newProjects);

      // Auto-select the first project if nothing is selected yet
      if (newProjects.length > 0 && !state.selectedItem) {
        commit('SET_SELECTED_ITEM', { item: newProjects[0], type: 'project' });
      }

    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  },

  async fetchContent({ state, commit }) {
    commit('SET_CONTENT_LOADING', true);

    try {
      let images = [];
      let imageGroups = [];
      let totalImagesCount = 0;

      if (!state.selectedItem) {
        commit('SET_CONTENT_DATA', { images: [], imageGroups: [] });
        commit('SET_TOTAL_IMAGES', 0);
        return;
      }

      const collectionParams = {
        nbPerPage: state.perPage
      };

      if (state.selectedItemType === 'project') {
        // Use already loaded image groups for display
        imageGroups = state.selectedItem.imageGroups || [];

        // Fetch paginated images for the project (not belonging to any image group directly)
        const imageInstanceCollection = new ImageInstanceCollection({
          ...collectionParams,
          filterKey: 'project',
          filterValue: state.selectedItem.id
        });

        await imageInstanceCollection.fetchPage(state.currentPage - 1);
        const fetchedImages = imageInstanceCollection.array;

        // Notice: This backend API might still include images that are IN groups. 
        // We filter them out locally for now, but totalNbItems might be slightly off.
        images = fetchedImages.filter(image => !image.imageGroup);
        totalImagesCount = imageInstanceCollection.totalNbItems; // total before local filtering
      } else if (state.selectedItemType === 'imageGroup') {
        // Fetch paginated images for the image group
        const imageInstanceCollection = new ImageInstanceCollection({
          ...collectionParams,
          filterKey: 'imagegroup',
          filterValue: state.selectedItem.id
        });

        await imageInstanceCollection.fetchPage(state.currentPage - 1);
        images = imageInstanceCollection.array;
        totalImagesCount = imageInstanceCollection.totalNbItems;
      }

      commit('SET_CONTENT_DATA', { images, imageGroups });
      commit('SET_TOTAL_IMAGES', totalImagesCount);
    } catch (error) {
      console.error('Error fetching content:', error);
      commit('SET_CONTENT_DATA', { images: [], imageGroups: [] });
      commit('SET_TOTAL_IMAGES', 0);
    } finally {
      commit('SET_CONTENT_LOADING', false);
    }
  },

  async fetchProjectDetails({ state, commit }) {
    if (state.selectedItemType !== 'project' || !state.selectedItem) {
      return;
    }

    try {
      const projectModel = new Project(state.selectedItem);
      // Fetch representatives
      const representatives = (await projectModel.fetchRepresentatives()).array;
      // Fetch online users
      const onlines = await projectModel.fetchConnectedUsers();

      commit('SET_PROJECT_DETAILS', { representatives, onlines });
    } catch (error) {
      console.error('Error fetching project details:', error);
      commit('SET_PROJECT_DETAILS', { representatives: [], onlines: [] });
    }
  },

  setAllFilter({ commit, dispatch }, all) {
    commit('SET_ALL_FILTER', all);
    return dispatch('fetchProjects');
  },

  setSortOrder({ commit, dispatch }, order) {
    commit('SET_SORT_ORDER', order);
    return dispatch('fetchProjects');
  },

  async toggleProject({ commit, state, dispatch }, project) {
    const proj = state.projects.find(p => p.id === project.id);
    if (proj) {
      commit('TOGGLE_PROJECT_EXPANSION', project);
    }
    // reset pagination
    commit('SET_CURRENT_PAGE', 1);
    commit('SET_SELECTED_ITEM', { item: project, type: 'project' });
  },

  async selectImageGroup({ commit, state, dispatch }, imageGroup) {
    const projectId = imageGroup.project;
    const project = state.projects.find(p => p.id === projectId);
    if (project && !project.isExpanded) {
      commit('EXPAND_PROJECT', project);
    }
    // reset pagination
    commit('SET_CURRENT_PAGE', 1);
    commit('SET_SELECTED_ITEM', { item: imageGroup, type: 'imageGroup' });
  },

  findProjectForImageGroup({ state }, imageGroup) {
    const projectId = imageGroup.project;
    return state.projects.find(p => p.id === projectId);
  },

  setActiveImageGroupForViewer({ commit }, groupId) {
    commit('SET_ACTIVE_IMAGE_GROUP_FOR_VIEWER', groupId);
  },

  setPage({ commit, dispatch }, page) {
    commit('SET_CURRENT_PAGE', page);
    return dispatch('fetchContent');
  },

  setPerPage({ commit, dispatch }, perPage) {
    commit('SET_PER_PAGE', perPage);
    commit('SET_CURRENT_PAGE', 1);
    return dispatch('fetchContent');
  },

  setProjectPage({ commit, dispatch }, page) {
    commit('SET_PROJECT_CURRENT_PAGE', page);
    return dispatch('fetchProjects');
  },

  setProjectPerPage({ commit, dispatch }, perPage) {
    commit('SET_PROJECT_PER_PAGE', perPage);
    commit('SET_PROJECT_CURRENT_PAGE', 1);
    return dispatch('fetchProjects');
  },
};

const getters = {
  filteredProjects: (state) => {
    return state.projects; // Handled by backend filter for main search
  },

  getFilteredImageGroups: (state) => (imageGroups) => {
    if (!state.searchString || state.searchString.trim() === '') {
      return imageGroups;
    }

    const searchLower = state.searchString.toLowerCase().trim();
    return imageGroups.filter(ig =>
      ig.name.toLowerCase().includes(searchLower)
    );
  },

  // Content display getters
  contentLoading: (state) => state.contentLoading,
  contentImages: (state) => state.images,
  contentImageGroups: (state) => state.imageGroups,

  // Pagination getters
  currentPage: (state) => state.currentPage,
  perPage: (state) => state.perPage,
  totalImages: (state) => state.totalImages,

  projectCurrentPage: (state) => state.projectCurrentPage,
  projectPerPage: (state) => state.projectPerPage,
  totalProjects: (state) => state.totalProjects,

  // Helper getter to find project for image group
  findProjectForImageGroup: (state) => (imageGroup) => {
    const projectId = imageGroup.project;
    return state.projects.find(p => p.id === projectId);
  }
};
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};